import {
  type ColumnDef,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import { useConfigurations } from "@via/api/configuration";
import { ActionButton } from "@via/components/action-button/action-button.tsx";
import Drawer from "@via/components/drawer.tsx";
import { GenericError } from "@via/components/error.tsx";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table, { TableSize } from "@via/components/table/table.tsx";
import {
  useExportUsersForCsv,
  useGetUsers,
  useUpdateUserByUserId,
  useDeleteUserByUserId,
} from "@via/pages/account/admin/admin.api.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import UserFilters from "@via/pages/account/admin/users/user-filters.tsx";
import CreateUserForm from "@via/pages/account/admin/users/components/create-user-form.tsx";
import ChangePasswordModal from "@via/pages/account/admin/users/components/change-password-modal.tsx";
import EditUserForm from "@via/pages/account/admin/users/components/edit-user-form.tsx";
import ResetMfaModal from "@via/pages/account/admin/users/components/reset-mfa-modal.tsx";
import { FilterItem } from "@via/pages/account/admin/utils/helper.ts";
import {
  BUTTONS,
  EMPTY_TABLE_MESSAGE,
  ERROR,
  USERS_TABLE_ACTIONS,
} from "@via/utils/constants.tsx";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import type { TableContextMenuInterface } from "@via/utils/interfaces.tsx";
import { OrderBy, UpdateUser, User, UserRole, UserStatus } from "common";
import { type JSX, useState, useCallback } from "react";
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading";

const columnHelper = createColumnHelper<User>();
const defaultParams = {
  page: 0,
  pageSize: 10,
  sort: [],
};

const getBooleanIcon = (value: boolean | undefined) => {
  if (value === true) {
    return (
      <div className="w-full flex items-center justify-center">
        <CheckCircleIcon className="h-6 w-6" aria-hidden="true" />
      </div>
    );
  } else {
    return (
      <div className="w-full flex items-center justify-center">
        <XCircleIcon className="h-6 w-6" aria-hidden="true" />
      </div>
    );
  }
};

export const getUserOptions = (
  user: User,
  isAdmin: boolean,
  currentUserId: number | undefined,
  mfaOn: boolean
) => {
  const options = [{ name: USERS_TABLE_ACTIONS.EDIT_USER, href: "#" }];

  const conditionalActions = [
    {
      flag: user.allowProjectBucket,
      enabledAction: USERS_TABLE_ACTIONS.DISABLE_PROJECT_BUCKET,
      disabledAction: USERS_TABLE_ACTIONS.ENABLE_PROJECT_BUCKET,
    },
    {
      flag: user.allowCostPrediction,
      enabledAction: USERS_TABLE_ACTIONS.DISABLE_COST_PREDICTION,
      disabledAction: USERS_TABLE_ACTIONS.ENABLE_COST_PREDICTION,
    },
    {
      flag: user.active,
      enabledAction: USERS_TABLE_ACTIONS.DEACTIVATE_USER,
      disabledAction: USERS_TABLE_ACTIONS.ACTIVATE_USER,
    },
  ];

  conditionalActions.forEach(({ flag, enabledAction, disabledAction }) => {
    if (typeof flag === "string") {
      if (flag !== UserStatus.Incomplete) {
        options.push({
          name: flag === UserStatus.Active ? enabledAction : disabledAction,
          href: "#",
        });
      }
    } else {
      options.push({
        name: flag === true ? enabledAction : disabledAction,
        href: "#",
      });
    }
  });
  options.push({ name: USERS_TABLE_ACTIONS.DELETE_USER, href: "#" });
  if (isAdmin) {
    options.push({ name: USERS_TABLE_ACTIONS.CHANGE_PASSWORD, href: "#" });
    // Resetting a second factor means nothing while the installation has MFA off.
    if (mfaOn && user.id !== currentUserId) {
      options.push({ name: USERS_TABLE_ACTIONS.RESET_MFA, href: "#" });
    }
  }

  return options;
};

export default function Users(): JSX.Element {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.userRole === UserRole.Admin;
  const { data: configurations } = useConfigurations();
  const mfaOn = (configurations?.MFA_ENFORCEMENT ?? "off") !== "off";
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [userToBeEdited, setUserToBeEdited] = useState<User | null>(null);
  const [userToChangePassword, setUserToChangePassword] = useState<User | null>(
    null
  );
  const [isResetMfaOpen, setIsResetMfaOpen] = useState(false);
  const [userToResetMfa, setUserToResetMfa] = useState<User | null>(null);

  const [page, setPage] = useState(defaultParams.page);
  const [pageSize, setPageSize] = useState(defaultParams.pageSize);
  const [sort, setSort] = useState<SortingState>(defaultParams.sort);
  const [columnFilters, setColumnFilters] = useState<FilterItem[]>([]);

  const sortFieldMap: Record<
    string,
    "id" | "username" | "dateCreated" | "dateModified"
  > = {
    id: "id",
    userName: "username",
  };
  const selectedSortField = sort[0]?.id ? sortFieldMap[sort[0].id] : undefined;
  const selectedSortOrder = sort[0]
    ? sort[0].desc
      ? OrderBy.Desc
      : OrderBy.Asc
    : undefined;

  const {
    data: usersList,
    isLoading: isGetUsersLoading,
    isError: isGetUsersError,
  } = useGetUsers({
    take: pageSize,
    skip: page * pageSize,
    sort: selectedSortField,
    order: selectedSortOrder,
    filter: columnFilters,
  });

  const { mutate: updateUser } = useUpdateUserByUserId();
  const { mutate: exportUsersForCsv, isPending: isExportUsersLoading } =
    useExportUsersForCsv();
  const { mutateAsync: deleteUserAsync, isPending: isDeleting } =
    useDeleteUserByUserId();

  // ---- delete modal state/handlers
  const [itemToDelete, setItemToDelete] = useState<User | null>(null);
  const openDeleteModal = (user: User) => setItemToDelete(user);

  const confirmDelete = useCallback(async () => {
    if (!itemToDelete) return;
    await deleteUserAsync({ userId: itemToDelete.id });
    setItemToDelete(null);
  }, [itemToDelete, deleteUserAsync]);

  const updateUserFlags = (userId: number, params: Partial<UpdateUser>) => {
    updateUser({ userId, params });
  };

  const handleAction = (
    option: TableContextMenuInterface,
    user: User
  ): void => {
    switch (option.name) {
      case USERS_TABLE_ACTIONS.EDIT_USER:
        setIsEditDrawerOpen(true);
        setUserToBeEdited(user);
        break;
      case USERS_TABLE_ACTIONS.ENABLE_PROJECT_BUCKET:
        updateUserFlags(user.id, { allowProjectBucket: true });
        break;
      case USERS_TABLE_ACTIONS.DISABLE_PROJECT_BUCKET:
        updateUserFlags(user.id, { allowProjectBucket: false });
        break;
      case USERS_TABLE_ACTIONS.ENABLE_COST_PREDICTION:
        updateUserFlags(user.id, { allowCostPrediction: true });
        break;
      case USERS_TABLE_ACTIONS.DISABLE_COST_PREDICTION:
        updateUserFlags(user.id, { allowCostPrediction: false });
        break;
      case USERS_TABLE_ACTIONS.ACTIVATE_USER:
        updateUserFlags(user.id, { active: true });
        break;
      case USERS_TABLE_ACTIONS.DEACTIVATE_USER:
        updateUserFlags(user.id, { active: false });
        break;
      case USERS_TABLE_ACTIONS.DELETE_USER:
        openDeleteModal(user);
        break;
      case USERS_TABLE_ACTIONS.CHANGE_PASSWORD:
        setUserToChangePassword(user);
        setIsChangePasswordModalOpen(true);
        break;
      case USERS_TABLE_ACTIONS.RESET_MFA:
        setUserToResetMfa(user);
        setIsResetMfaOpen(true);
        break;
      default:
        break;
    }
  };

  const columns = [
    columnHelper.accessor("id", {
      cell: info => info.getValue(),
      header: () => <span>Id</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("userName", {
      cell: info => info.getValue(),
      header: () => <span>User Name</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("name", {
      cell: info => info.getValue(),
      header: () => <span>Name</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("email", {
      cell: info => info.getValue(),
      header: () => <span>Email</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("institute", {
      cell: info => info.getValue(),
      header: () => <span>Institute</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("lab", {
      cell: info => info.getValue(),
      header: () => <span>Lab</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("role", {
      cell: info => info.getValue(),
      header: () => <span>Role</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("allowProjectBucket", {
      cell: ({ row }) => getBooleanIcon(row.original.allowProjectBucket),
      header: () => <span>Allow Project Bucket</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("allowCostPrediction", {
      cell: ({ row }) => getBooleanIcon(row.original.allowCostPrediction),
      header: () => <span>Allow Cost Prediction</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("active", {
      cell: info => info.getValue(),
      header: () => <span>Status</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("id", {
      header: () => "Options",
      cell: info => {
        const usersTableOptions = getUserOptions(
          info.row.original,
          isAdmin,
          currentUser?.id,
          mfaOn
        );

        return (
          <div className="w-full flex items-center justify-center">
            <TableContextMenu
              options={usersTableOptions}
              onSelect={option => {
                handleAction(option, info.row.original);
              }}
            />
          </div>
        );
      },
      enableSorting: false,
    }),
  ] as Array<ColumnDef<User>>;

  return (
    <>
      <div className="p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg">
        <div className="via-page-heading-wrapper">
          <h2 className="via-h2">Users</h2>
          <div className="flex gap-2">
            <ActionButton
              button={BUTTONS.CREATE_USER}
              onClick={() => {
                setIsEditDrawerOpen(false);
                setIsCreateDrawerOpen(true);
              }}
            />
            <button
              id="downloadUsersIcon"
              type="button"
              disabled={isExportUsersLoading}
              className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out hover:bg-indigo-500 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-70"
              onClick={() => {
                exportUsersForCsv({
                  sort: selectedSortField,
                  order: selectedSortOrder,
                  filter: columnFilters,
                  columns,
                });
              }}>
              <ArrowDownTrayIcon
                className="-ml-0.5 h-5 w-5"
                aria-hidden="true"
              />
              <Spinner loading={isExportUsersLoading}>
                {BUTTONS.EXPORT_DATA}
              </Spinner>
            </button>
          </div>
        </div>

        {isGetUsersError ? (
          <div className="via-generic-error">
            <GenericError message={ERROR} />
          </div>
        ) : (
          <>
            <Table
              isLoading={isGetUsersLoading}
              data={usersList?.data || []}
              columns={columns}
              sort={sort}
              setSort={setSort}
              page={page}
              setPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              total={usersList?.total}
              size={TableSize.Big}
              emptyMessage={EMPTY_TABLE_MESSAGE.USER_LIST}
              additionalFilters={
                <UserFilters
                  setColumnFilters={setColumnFilters}
                  setPage={setPage}
                  setPageSize={setPageSize}
                />
              }
            />
          </>
        )}
        <Drawer
          id="create-user"
          open={isCreateDrawerOpen}
          setOpen={setIsCreateDrawerOpen}>
          <CreateUserForm setOpen={setIsCreateDrawerOpen} />
        </Drawer>
        <Drawer
          id="edit-user"
          open={isEditDrawerOpen}
          setOpen={setIsEditDrawerOpen}>
          <EditUserForm setOpen={setIsEditDrawerOpen} user={userToBeEdited} />
        </Drawer>
        <DeleteModal
          item={itemToDelete}
          setItem={setItemToDelete}
          deleteFunction={confirmDelete}
          isLoading={isDeleting}
          title="Delete user?"
          message={name => (
            <>
              This will permanently remove{" "}
              <strong className="font-semibold">{name}</strong>. This action
              cannot be undone.
            </>
          )}
          confirmText="Delete"
        />
        <ChangePasswordModal
          open={isChangePasswordModalOpen}
          setOpen={setIsChangePasswordModalOpen}
          userToChangePassword={userToChangePassword}
          setUserToChangePassword={setUserToChangePassword}
        />
        <ResetMfaModal
          open={isResetMfaOpen}
          setOpen={setIsResetMfaOpen}
          user={userToResetMfa}
          onClosed={() => setUserToResetMfa(null)}
        />
      </div>
    </>
  );
}
